import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

import socket from 'src/components/common/WebSocketConnection/websocket';
import useSocketListener from 'src/hooks/useSocketListener';
import { playSound } from 'src/modules/soundHandler';
import { setAppView } from 'src/store/app/actions';
import { setPlayedGames } from 'src/store/app/actions';
import GameSquare from 'src/components/gamesCreator/GameSquareV2';
import BlackOverlay from 'src/components/common/BlackOverlay';
import MovieCountdown from 'src/components/common/MovieCountdown';
import type { GameType } from 'src/components/gamesCreator/GameSquareV2/types';
// eslint-disable-next-line max-len
import ShowcaseActions from 'src/components/gamesCreator/GameSquareV2/ShowcaseActions';

import './InLobby.scss';

interface InLobbyBoxPropsInterface {
  joinCode?: string;
  minPlayers: number | string;
  maxPlayers: number | string;
  isPrivateLobby: boolean;
  leaveLobby: () => unknown;
}

const InLobbyBox = ({
  joinCode,
  minPlayers,
  maxPlayers,
  isPrivateLobby,
  leaveLobby,
}: InLobbyBoxPropsInterface) => {
  const [players, setPlayers] = useState<
    {
      nickname: string;
      isMe: boolean;
      color: string;
      isReady: boolean;
      elo: number;
    }[]
  >([
    { nickname: '(Me)', isMe: true, color: 'green', isReady: false, elo: -1 },
  ]);
  const [games, setGames] = useState<GameType[]>([]);
  const [countdownDuration, setCountdownDuration] = useState(-1);
  const [gameStartCountdown, setGameStartCountdown] = useState(false);

  const dispatch = useDispatch();

  const inLobbySocketListener = (event: any) => {
    const action = JSON.parse(event.data);
    const { type, payload } = action;

    switch (type) {
      case 'SEND_GAMES_IN_LOBBY': {
        const { gamesInLobby } = payload;
        setGames(gamesInLobby);
        break;
      }

      case 'SEND_PLAYERS_IN_LOBBY': {
        const { playersInLobby } = payload;
        if (playersInLobby.length > players.length) {
          // Player joined
          playSound('playerJoinedLobby');
        } else if (playersInLobby.length < players.length) {
          // Player left
          playSound('playerLeftLobby');

          if (gameStartCountdown) setGameStartCountdown(false);
        }
        setPlayers(playersInLobby);
        break;
      }

      case 'LOBBY_COUNTDOWN_START': {
        const { joinCode: rJoinCode, seconds } = payload;
        if (rJoinCode === joinCode) {
          setCountdownDuration(seconds);
          setGameStartCountdown(true);
        }
        break;
      }

      case 'GAME_STARTING': {
        const { battleId, games } = payload;
        dispatch(setPlayedGames(battleId, games));
        dispatch(setAppView('game')); // TODO: Temp?
        break;
      }

      default:
        break;
    }
  };

  useSocketListener(
    'InLobbyBox',
    'message',
    useCallback(inLobbySocketListener, [players, gameStartCountdown]),
  );

  useEffect(() => {
    // TODO: Add joining lobby via url

    return () => {
      socket.sendJSON({
        type: 'LEAVE_LOBBY',
        payload: {
          joinCode,
        },
      });
    };
  }, []);

  const sendReadyStatus = (isReady: boolean) => {
    socket.sendJSON({
      type: 'READY_IN_LOBBY',
      payload: {
        joinCode,
        isReady,
      },
    });
  };

  const mePlayer = players.find((player) => player.isMe);

  return (
    <div className="InLobbyBox">
      <div className="games-list">
        {games.map((game, i) => (
          <GameSquare
            // TODO: Add spotting if settings are not default
            // (small circle indicator)
            key={i}
            type="showcase"
            game={game}
            actions={<ShowcaseActions game={game} />}
          />
        ))}
      </div>
      <div className="lobby-details">
        <div className="join-code-holder">
          <div className="text">Join code</div>
          <div className="code">{joinCode}</div>
        </div>
        <div className="basic-box">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Chip
              color="primary"
              label={isPrivateLobby ? 'Private Lobby' : 'Public Lobby'}
            />
            <div className="players-count">
              Players:
              <span className="highlight">
                {players.length}/{maxPlayers}
              </span>
              <span>(min: {minPlayers})</span>
              <div className="progress-bar">
                <div
                  className={`fill ${
                    players.length < Number(minPlayers)
                      ? 'not-enough-players'
                      : ''
                  }`}
                  style={{
                    width: `${(players.length / Number(maxPlayers)) * 100}%`,
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
        <div className="basic-box">
          <span className="small-text">
            The battle will start when all players are ready and minimum players
            required is met.
          </span>
        </div>
        <div style={{ display: 'flex' }}>
          <Button
            variant={mePlayer?.isReady ? 'outlined' : 'contained'}
            onClick={() => sendReadyStatus(!mePlayer?.isReady)}
            sx={{
              borderRadius: 0,
              height: '50px',
              fontWeight: 'bold',
              flexGrow: mePlayer?.isReady ? 0 : 5,
              transition: 'flex 0.3s',
            }}
          >
            {mePlayer?.isReady ? 'Unready' : 'Ready up'}
          </Button>
          <Box
            sx={{
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              marginLeft: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 8px',
              flexGrow: 1,
            }}
          >
            {mePlayer?.isReady ? (
              <CheckCircleIcon fontSize="large" />
            ) : (
              <HourglassEmptyIcon fontSize="large" />
            )}
          </Box>
        </div>
        <div className="basic-box">Players:</div>
        {players.map((player) => (
          <div
            key={player.nickname}
            className={['basic-box', 'player-box', player.isMe && 'is-me']
              .filter(Boolean)
              .join(' ')}
          >
            <div className="player-info">
              <div className={['color-box', player.color].join(' ')}></div>
              <div className="player-details">
                <div className="player-nick">
                  {player.nickname}{' '}
                  {player.isMe && <span className="sidenote">(You)</span>}
                </div>
                <div className="elo">
                  Elo: {player.elo >= 0 ? player.elo : '?'}
                </div>
              </div>
            </div>
            <div className="player-status">
              <Chip
                color={player.isReady ? 'primary' : 'error'}
                label={player.isReady ? 'Ready' : 'Not ready'}
                size="small"
              />
              {player.isReady ? (
                <CheckCircleIcon color="primary" sx={{ fontSize: '16px' }} />
              ) : (
                <HourglassEmptyIcon color="error" sx={{ fontSize: '16px' }} />
              )}
            </div>
          </div>
        ))}
      </div>
      {gameStartCountdown && (
        <BlackOverlay>
          <MovieCountdown
            duration={countdownDuration}
            onCountdownEnd={() => setGameStartCountdown(false)}
          />
          <Button variant="outlined" onClick={leaveLobby}>
            Leave Lobby
          </Button>
        </BlackOverlay>
      )}
    </div>
  );
};

export default InLobbyBox;
